import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { User, Calendar, CalendarRange, LogOut, TrendingUp, Medal, ChevronDown, Target, PartyPopper, CheckCircle, Lock, MessageCircle, HandHeart, QrCode, Instagram, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { LOGO_RAIZES_WHITE } from '../../lib/branding';
import { LOGO_SEMENTE } from '../../lib/branding';
import { VerseCard } from '../../components/VerseCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPais = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [children, setChildren] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [activities, setActivities] = useState({ daily: [], weekly: [] });
  const [extras, setExtras] = useState([]);
  const [lastSeason, setLastSeason] = useState(null);
  const [seasonStatus, setSeasonStatus] = useState({ is_closed: false, season_end: null });
  const [parentData, setParentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextMeeting, setNextMeeting] = useState(null);
  const [whatsappLinks, setWhatsappLinks] = useState(null);
  const [goals, setGoals] = useState(null);
  const selectedIdxRef = useRef(0);

  const fetchAllData = useCallback(async (tkn) => {
    const token = tkn || localStorage.getItem('parent_token');
    if (!token) return;
    try {
      const childrenRes = await axios.get(`${API}/parent/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const kids = childrenRes.data.children || [];
      setChildren(kids);

      if (kids.length > 0) {
        const idx = selectedIdxRef.current < kids.length ? selectedIdxRef.current : 0;
        const actRes = await axios.get(`${API}/parent/child/activities?child_id=${kids[idx].child.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setActivities(actRes.data);

        // Fetch Sementes Extras
        axios.get(`${API}/parent/extra-seeds`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => setExtras(r.data || [])).catch(() => setExtras([]));

        // Fetch last-season result (arquivada) — mostra ranking final fixo
        axios.get(`${API}/parent/last-season-result`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => setLastSeason(r.data)).catch(() => setLastSeason(null));

        // Fetch current-season status (para bloquear devocional quando encerrado)
        axios.get(`${API}/scoring/current-season-public`)
          .then(r => setSeasonStatus({ is_closed: !!r.data?.is_closed, season_end: r.data?.season_end || null }))
          .catch(() => {});

        // Fetch WhatsApp links with child context
        axios.get(`${API}/groups/whatsapp-links?child_id=${kids[idx].child.id}`).then(r => setWhatsappLinks(r.data)).catch(() => {});

        // Fetch goals from projeto-sementes
        axios.get(`${API}/reports/projeto-sementes`).then(r => {
          const childGoals = (r.data.children || []).find(c => c.child_id === kids[idx].child.id);
          if (childGoals) setGoals(childGoals.goals);
        }).catch(() => {});
      } else {
        // Fetch WhatsApp links without child context
        axios.get(`${API}/groups/whatsapp-links`).then(r => setWhatsappLinks(r.data)).catch(() => {});
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    const data = localStorage.getItem('parent_data');
    
    if (!token || !data) {
      navigate('/app-pais/login');
      return;
    }
    
    setParentData(JSON.parse(data));
    setLoading(true);
    fetchAllData(token);
    axios.get(`${API}/agenda/next-meeting`).then(r => setNextMeeting(r.data.next)).catch(() => {});
  }, [navigate, fetchAllData, location.key]);

  const switchChild = async (idx) => {
    setSelectedIdx(idx);
    selectedIdxRef.current = idx;
    const token = localStorage.getItem('parent_token');
    const kid = children[idx];
    if (kid && token) {
      try {
        const actRes = await axios.get(`${API}/parent/child/activities?child_id=${kid.child.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setActivities(actRes.data);
        axios.get(`${API}/groups/whatsapp-links?child_id=${kid.child.id}`).then(r => setWhatsappLinks(r.data)).catch(() => {});
        // Update goals for switched child
        axios.get(`${API}/reports/projeto-sementes`).then(r => {
          const childGoals = (r.data.children || []).find(c => c.child_id === kid.child.id);
          if (childGoals) setGoals(childGoals.goals);
          else setGoals(null);
        }).catch(() => {});
      } catch (e) {
        console.error('Erro ao carregar atividades:', e);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('parent_token');
    localStorage.removeItem('parent_data');
    navigate('/app-pais/login');
  };

  const getMedalIcon = (medalha) => {
    const medals = { 'ouro': '1', 'prata': '2', 'bronze': '3', 'nenhuma': '' };
    return medals[medalha] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando...</p>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex flex-col items-center justify-center px-6">
        <p className="font-inter text-gray-500 mb-4">Nenhum filho encontrado.</p>
        <button
          onClick={handleLogout}
          className="bg-gray-200 text-gray-700 px-6 py-2 rounded-full font-semibold text-sm"
          data-testid="logout-error-button"
        >
          Sair
        </button>
      </div>
    );
  }

  const current = children[selectedIdx];
  const child = current.child;
  const score = current.score;

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-6 py-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <img src={LOGO_RAIZES_WHITE} alt="Ministério Raízes" className="h-12 object-contain" />
            <div className="flex items-center gap-3">
              <img src={LOGO_SEMENTE} alt="Projeto Sementes" className="h-28 object-contain" />
              <button
                onClick={handleLogout}
                className="hover:bg-white/10 rounded-full p-2 transition-all"
                title="Sair"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          <h2 className="font-nunito font-extrabold text-2xl text-center tracking-widest text-[#F4D35E] mb-2">PORTAL DA FAMÍLIA</h2>
          <div>
            <h1 className="font-nunito font-bold text-xl">Olá, {parentData?.parent_name}!</h1>
            <p className="font-inter text-white/80 text-sm">Acompanhe o crescimento espiritual do seu filho!</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Next Meeting Card */}
        {nextMeeting && (
          <Card data-testid="parent-next-meeting-card" className="bg-gradient-to-r from-[#F4D35E] to-[#E8C74A] rounded-2xl p-5 shadow-lg text-[#1A4D2E]">
            <div className="flex items-start gap-4">
              <div className="bg-[#1A4D2E]/15 rounded-xl p-3 shrink-0">
                <CalendarRange className="w-6 h-6 text-[#1A4D2E]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-inter text-[#1A4D2E]/60 text-xs uppercase tracking-wider mb-1">Próximo Encontro</p>
                <p className="font-nunito font-bold text-lg text-[#1A4D2E]">
                  {nextMeeting.data ? new Date(nextMeeting.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }) : `Semana ${nextMeeting.semana}`}
                </p>
                {(nextMeeting.horario_inicio || nextMeeting.horario) && (
                  <p className="font-inter text-[#1A4D2E]/70 text-sm mt-0.5">
                    {nextMeeting.horario_inicio || nextMeeting.horario}{nextMeeting.horario_fim ? ` - ${nextMeeting.horario_fim}` : ''}
                  </p>
                )}
                <p className="font-inter text-[#1A4D2E] font-semibold text-sm mt-2">{nextMeeting.programacao || 'A definir'}</p>
                <p className="font-inter text-[#1A4D2E]/70 text-sm">Tema: {nextMeeting.tema || 'A definir'}</p>
                {nextMeeting.pregador && (
                  <p className="font-inter text-[#1A4D2E]/60 text-sm">Pregador: {nextMeeting.pregador}</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Verse of the Week */}
        <VerseCard isAdmin={false} />

        {/* Prayer Requests Card */}
        <Card
          data-testid="parent-prayer-card"
          className="bg-gradient-to-r from-amber-700 to-amber-600 rounded-2xl p-5 shadow-lg text-white cursor-pointer hover:shadow-xl transition-all"
          onClick={() => navigate('/oracoes')}
        >
          <div className="flex items-center gap-4">
            <div className="bg-white/15 rounded-xl p-3 shrink-0">
              <HandHeart className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-nunito font-bold text-lg">Pedidos de Oração</p>
              <p className="text-white/70 text-sm">Faça seu pedido ou veja seus pedidos</p>
            </div>
          </div>
        </Card>

        {/* Child Selector (if multiple) */}
        {children.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {children.map((k, idx) => (
              <button
                key={k.child.id}
                data-testid={`child-tab-${idx}`}
                onClick={() => switchChild(idx)}
                className={`flex-shrink-0 px-5 py-2 rounded-full font-semibold text-sm transition-all ${
                  idx === selectedIdx
                    ? 'bg-[#1A4D2E] text-white shadow-md'
                    : 'bg-white text-[#1A4D2E] border border-[#1A4D2E]/20'
                }`}
              >
                {k.child.nome}
              </button>
            ))}
          </div>
        )}

        {/* Last Season Result (fixado apos encerramento) */}
        {(() => {
          const myResult = lastSeason?.results?.find(r => r.child_id === child.id);
          if (!lastSeason || !myResult) return null;
          const medalMap = { ouro: '🥇', prata: '🥈', bronze: '🥉' };
          return (
            <Card data-testid="last-season-card" className="bg-gradient-to-br from-[#F4D35E]/20 to-[#F2F7F4] rounded-2xl p-5 shadow-lg border-2 border-[#F4D35E]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🏆</span>
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Resultado Final</h3>
                <span className="ml-auto text-xs bg-white text-[#1A4D2E] font-semibold px-2 py-0.5 rounded-full">Encerrado</span>
              </div>
              <p className="font-nunito font-bold text-base text-[#1A4D2E]">{lastSeason.season_name}</p>
              <p className="font-inter text-xs text-gray-600 mb-3">
                {lastSeason.start_date} a <strong>{lastSeason.end_date}</strong>
              </p>
              <div className="bg-white/70 rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-inter text-xs text-gray-600">Posicao final</p>
                  <p className="font-nunito font-bold text-2xl text-[#1A4D2E]">
                    {myResult.posicao_final ? `${myResult.posicao_final}o lugar` : '-'}
                  </p>
                  <p className="font-inter text-xs text-gray-500">
                    de {lastSeason.total_children} criancas
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-inter text-xs text-gray-600">Sementes</p>
                  <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{myResult.total_pontos}</p>
                </div>
                {myResult.medalha && medalMap[myResult.medalha] && (
                  <div className="text-4xl" title={myResult.medalha}>{medalMap[myResult.medalha]}</div>
                )}
              </div>
            </Card>
          );
        })()}

        {/* Child Info Card */}
        <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              {child.foto_url ? (
                <img src={child.foto_url.startsWith('http') ? child.foto_url : `${API.replace('/api','')}${child.foto_url}`} alt={child.nome}
                  className="w-16 h-16 rounded-2xl object-cover border-2 border-[#E1ECE5] shadow-sm"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className={`w-16 h-16 rounded-2xl bg-[#F2F7F4] items-center justify-center ${child.foto_url ? 'hidden' : 'flex'}`}>
                <User className="w-8 h-8 text-[#1A4D2E]" />
              </div>
              <div>
                <h2 className="font-nunito font-bold text-3xl text-[#1A4D2E]" data-testid="child-name">
                  {child.nome}
                </h2>
                <p className="font-inter text-gray-600">
                  {child.idade} {child.idade === 1 ? 'ano' : 'anos'}
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-1">
                {score.medalha === 'ouro' ? '1' : score.medalha === 'prata' ? '2' : score.medalha === 'bronze' ? '3' : ''}
              </div>
              <p className="font-inter text-xs text-gray-500 capitalize">{score.medalha}</p>
            </div>
          </div>

          <div className="bg-[#F2F7F4] rounded-xl p-4 mb-4">
            <p className="font-inter text-sm text-gray-600 mb-1">Total de Sementes</p>
            <div className="flex items-end gap-2">
              <p className="font-nunito font-bold text-4xl text-[#1A4D2E]">
                {score.total_pontos}
              </p>
              <TrendingUp className="w-6 h-6 text-[#1A4D2E] mb-1" />
            </div>
          </div>

          {/* Progress bars per criterion */}
          <div className="space-y-3">
            {[
              { label: 'Devocional Diario', atual: score.total_devocionais, meta: goals?.devocional?.meta },
              { label: 'Presenca Semanal', atual: score.total_presencas, meta: goals?.presenca?.meta },
              { label: 'Pontualidade', atual: score.total_pontualidade, meta: goals?.pontualidade?.meta },
              { label: 'Levar Biblia', atual: score.total_biblia, meta: goals?.biblia?.meta },
              { label: 'Decorar Versiculo', atual: score.total_versiculos, meta: goals?.versiculo?.meta },
              { label: 'Levar Visitante', atual: score.total_visitas, meta: goals?.visitante?.meta },
              { label: 'Gincana Biblica', atual: score.total_gincana, meta: goals?.gincana?.meta },
              { label: 'Se Vira nos 5', atual: score.total_se_vira || 0, meta: goals?.se_vira?.meta },
            ].map((item, idx) => {
              const meta = item.meta || 1;
              const pct = Math.min(100, Math.round((item.atual / meta) * 100));
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-inter text-xs text-gray-600">{item.label}</span>
                    <span className="font-nunito font-bold text-xs text-[#1A4D2E]">{item.atual}/{meta} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div className="bg-[#1A4D2E] h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Sementes Extras Recentes */}
        {(() => {
          const childExtras = (extras || []).filter(e => e.child_id === child.id).slice(0, 8);
          if (childExtras.length === 0) return null;
          return (
            <Card data-testid="extras-card" className="bg-white rounded-2xl p-5 shadow-lg border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🌱</span>
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">
                  Sementes Extras
                </h3>
                <span className="ml-auto bg-emerald-100 text-emerald-800 font-bold text-sm px-3 py-1 rounded-full">
                  +{childExtras.reduce((s, e) => s + (e.quantidade || 0), 0)}
                </span>
              </div>
              <p className="font-inter text-xs text-gray-500 mb-3">
                Sementes ganhas em campanhas pontuais
              </p>
              <div className="space-y-2">
                {childExtras.map((e) => (
                  <div
                    key={e.id}
                    data-testid={`extra-entry-${e.id}`}
                    className="flex items-start gap-3 bg-emerald-50/50 rounded-xl p-3 border border-emerald-100"
                  >
                    <div className="bg-[#F4D35E] text-[#1A4D2E] font-bold text-sm rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                      +{e.quantidade}
                    </div>
                    <div className="flex-1 min-w-0">
                      {e.campanha_nome && (
                        <p className="font-nunito font-bold text-sm text-emerald-800">
                          {e.campanha_nome}
                        </p>
                      )}
                      <p className="font-inter text-sm text-gray-700 break-words">
                        {e.justificativa}
                      </p>
                      <p className="font-inter text-xs text-gray-500 mt-1">
                        {e.data}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })()}

        {/* Weekly Goal */}
        {(() => {
          const WEEKLY_GOAL = 7;
          const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
          const brNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
          const dayOfWeek = brNow.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
          const startOfWeek = new Date(brNow);
          startOfWeek.setDate(brNow.getDate() - dayOfWeek); // Volta até o Domingo
          startOfWeek.setHours(0, 0, 0, 0);

          const todayStr = `${brNow.getFullYear()}-${String(brNow.getMonth() + 1).padStart(2, '0')}-${String(brNow.getDate()).padStart(2, '0')}`;

          // Build array of 7 days with date strings
          const weekDays = DAY_LABELS.map((label, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const done = activities.daily.some(a => {
              if (!a.devocional) return false;
              const actDate = typeof a.data === 'string' ? a.data.split('T')[0] : '';
              return actDate === dateStr;
            });
            const isFuture = dateStr > todayStr;
            const isToday = dateStr === todayStr;
            return { label, dateStr, done, isFuture, isToday, dayNum: d.getDate() };
          });

          const weeklyCount = weekDays.filter(d => d.done).length;
          const progress = Math.min(weeklyCount / WEEKLY_GOAL, 1);
          const goalReached = weeklyCount >= WEEKLY_GOAL;

          return (
            <Card data-testid="weekly-goal-card" className={`rounded-2xl p-5 shadow-lg border ${goalReached ? 'bg-gradient-to-r from-[#F4D35E]/20 to-[#F4D35E]/5 border-[#F4D35E]' : 'bg-white border-[#E1ECE5]'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {goalReached ? <PartyPopper className="w-5 h-5 text-[#F4D35E]" /> : <Target className="w-5 h-5 text-[#1A4D2E]" />}
                  <h3 className="font-nunito font-bold text-base text-[#1A4D2E]">Meta Semanal</h3>
                </div>
                <span data-testid="weekly-goal-count" className="font-nunito font-bold text-lg text-[#1A4D2E]">{weeklyCount}/{WEEKLY_GOAL}</span>
              </div>

              {/* Day-by-day visual */}
              <div className="grid grid-cols-7 gap-1.5 mb-3" data-testid="weekly-days-grid">
                {weekDays.map((d) => (
                  <div key={d.dateStr} className="flex flex-col items-center gap-1" data-testid={`day-${d.label.toLowerCase()}`}>
                    <span translate="no" className={`text-[10px] font-semibold notranslate ${d.isToday ? 'text-[#1A4D2E]' : 'text-gray-400'}`}>{d.label}</span>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                      d.done
                        ? 'bg-[#1A4D2E] text-white'
                        : d.isFuture
                          ? 'bg-gray-100 text-gray-300 border border-dashed border-gray-200'
                          : d.isToday
                            ? 'bg-amber-50 text-amber-600 border-2 border-amber-300'
                            : 'bg-red-50 text-red-400 border border-red-200'
                    }`}>
                      {d.done ? (
                        <CheckCircle className="w-4.5 h-4.5" />
                      ) : (
                        <span className="text-xs font-bold">{d.dayNum}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  data-testid="weekly-goal-bar"
                  className={`h-2.5 rounded-full transition-all duration-500 ${goalReached ? 'bg-[#F4D35E]' : 'bg-[#1A4D2E]'}`}
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              {goalReached ? (
                <p data-testid="weekly-goal-message" className="font-inter text-sm text-[#1A4D2E] font-semibold mt-2 text-center">
                  Parabéns! Meta da semana concluída!
                </p>
              ) : (
                <p className="font-inter text-xs text-gray-500 mt-2">
                  Faltam {WEEKLY_GOAL - weeklyCount} {WEEKLY_GOAL - weeklyCount === 1 ? 'devocional' : 'devocionais'} para completar a meta
                </p>
              )}
            </Card>
          );
        })()}

        {/* Quick Action */}
        {(() => {
          const _brNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
          const todayStr = `${_brNow.getFullYear()}-${String(_brNow.getMonth() + 1).padStart(2, '0')}-${String(_brNow.getDate()).padStart(2, '0')}`;
          const todayDone = activities.daily.some(a => {
            const actDate = typeof a.data === 'string' ? a.data.split('T')[0] : '';
            return actDate === todayStr && a.devocional;
          });
          return todayDone ? (
            <div data-testid="btn-already-done" className="w-full bg-gray-100 text-gray-500 rounded-2xl p-5 text-center border border-gray-200">
              <CheckCircle className="w-7 h-7 mx-auto mb-2 text-[#1A4D2E]" />
              <p className="font-nunito font-bold text-lg text-[#1A4D2E]">Devocional de hoje registrado</p>
              <p className="font-inter text-gray-500 text-sm">Volte amanhã para registrar novamente</p>
            </div>
          ) : seasonStatus.is_closed ? (
            <div data-testid="btn-season-closed" className="w-full bg-red-50 rounded-2xl p-5 text-center border-2 border-red-200">
              <p className="font-nunito font-bold text-lg text-red-700">Ciclo encerrado</p>
              <p className="font-inter text-gray-600 text-sm">
                {seasonStatus.season_end ? `Encerrado em ${seasonStatus.season_end}. ` : ''}
                Aguarde o próximo ciclo para registrar devocionais.
              </p>
            </div>
          ) : (
            <button
              data-testid="btn-register-daily"
              onClick={() => navigate(`/app-pais/lancamento?child_id=${child.id}`)}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-2xl p-5 shadow-lg active:scale-[0.98] transition-all text-center"
            >
              <Calendar className="w-7 h-7 mx-auto mb-2" />
              <p className="font-nunito font-bold text-lg">Registrar Devocional</p>
              <p className="font-inter text-white/70 text-sm">Marque as atividades diárias</p>
            </button>
          );
        })()}

        {/* Check-in QR Code */}
        <button
          data-testid="btn-checkin-qr"
          onClick={() => navigate('/checkin-pais')}
          className="w-full bg-gradient-to-r from-[#F4D35E] to-[#E8C74A] hover:from-[#e8c84e] hover:to-[#dabb3e] text-[#1A4D2E] rounded-2xl p-5 shadow-lg active:scale-[0.98] transition-all text-center"
        >
          <QrCode className="w-7 h-7 mx-auto mb-2" />
          <p className="font-nunito font-bold text-lg">Check-in do Encontro</p>
          <p className="font-inter text-[#1A4D2E]/70 text-sm">Gerar QR Code para o check-in</p>
        </button>

        {/* WhatsApp Groups */}
        <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5]" data-testid="whatsapp-links-card">
          <h3 className="font-nunito font-bold text-base text-[#1A4D2E] mb-3 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#25D366]" /> Grupos do WhatsApp
          </h3>
          <div className="space-y-2">
            <a
              href={(whatsappLinks && whatsappLinks.pais_raizes) || "https://chat.whatsapp.com/LtEsPsasezk3knsJrsgW6M?mode=gi_t"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#25D366]/10 hover:bg-[#25D366]/20 rounded-xl p-3 transition-all"
              data-testid="whatsapp-pais-raizes"
            >
              <div className="p-2 rounded-xl bg-[#25D366]">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-inter font-semibold text-sm text-[#1A4D2E]">Pais Raizes</p>
                <p className="font-inter text-[10px] text-gray-500">Grupo geral dos pais</p>
              </div>
            </a>
            {whatsappLinks?.child_group?.whatsapp_link && (
              <a
                href={whatsappLinks.child_group.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-[#1A4D2E]/5 hover:bg-[#1A4D2E]/10 rounded-xl p-3 transition-all"
                data-testid="whatsapp-child-group"
              >
                <div className="p-2 rounded-xl bg-[#1A4D2E]">
                  <HandHeart className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-inter font-semibold text-sm text-[#1A4D2E]">{whatsappLinks.child_group.group_name}</p>
                  <p className="font-inter text-[10px] text-gray-500">Grupo do discipulado</p>
                </div>
              </a>
            )}
          </div>
        </Card>

        {/* Instagram Links */}
        <Card className="bg-white rounded-2xl p-5 shadow-lg border border-[#E1ECE5]" data-testid="instagram-links-card">
          <h3 className="font-nunito font-bold text-base text-[#1A4D2E] mb-3 flex items-center gap-2">
            <Instagram className="w-5 h-5 text-[#E1306C]" /> Siga no Instagram
          </h3>
          <div className="space-y-2">
            {[
              { name: 'Raizes', handle: '@raizes.if', url: 'https://instagram.com/raizes.if', color: 'bg-[#1A4D2E]' },
              { name: 'Clubinho', handle: '@clubinhoif', url: 'https://instagram.com/clubinhoif', color: 'bg-[#F4D35E]' },
              { name: 'Igreja Familia', handle: '@igrejafamiliasorocaba', url: 'https://instagram.com/igrejafamiliasorocaba', color: 'bg-[#5A7A3B]' },
            ].map((ig) => (
              <a
                key={ig.handle}
                href={ig.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-xl p-3 transition-all"
                data-testid={`instagram-${ig.handle}`}
              >
                <div className={`p-2 rounded-xl ${ig.color}`}>
                  <Instagram className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-inter font-semibold text-sm text-[#1A4D2E]">{ig.name}</p>
                  <p className="font-inter text-[10px] text-gray-500">{ig.handle}</p>
                </div>
              </a>
            ))}
          </div>
        </Card>

        {/* Editar Perfil da Familia */}
        <button
          data-testid="btn-edit-profile"
          onClick={() => navigate('/app-pais/editar-perfil')}
          className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 shadow-md border border-[#E1ECE5] hover:bg-gray-50 transition-all"
        >
          <div className="p-2 rounded-xl bg-[#F4D35E]">
            <Pencil className="w-5 h-5 text-[#1A4D2E]" />
          </div>
          <span className="font-inter font-semibold text-[#1A4D2E]">Editar Perfil da Familia</span>
        </button>

        {/* Alterar Senha */}
        <button
          data-testid="btn-change-password"
          onClick={() => navigate('/app-pais/alterar-senha')}
          className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 shadow-md border border-[#E1ECE5] hover:bg-gray-50 transition-all"
        >
          <div className="p-2 rounded-xl bg-[#1A4D2E]">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <span className="font-inter font-semibold text-[#1A4D2E]">Alterar Senha</span>
        </button>

        {/* Fale Conosco */}
        <button
          data-testid="btn-fale-conosco"
          onClick={() => navigate('/fale-conosco')}
          className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 shadow-md border border-[#E1ECE5] hover:bg-gray-50 transition-all"
        >
          <div className="p-2 rounded-xl bg-[#5A7A3B]">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <span className="font-inter font-semibold text-[#1A4D2E]">Fale Conosco</span>
        </button>

        {/* Recent Activities */}
        <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
          <h3 className="font-nunito font-bold text-xl text-[#1A4D2E] mb-4">Atividades Recentes</h3>
          {activities.daily.length > 0 ? (
            <div className="space-y-3">
              {activities.daily.slice(0, 5).map((a, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="font-inter text-sm text-gray-500">
                      {new Date(a.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                    </p>
                    <div className="flex gap-2 mt-1">
                      {a.devocional && <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">Devocional</span>}
                    </div>
                  </div>
                  <span className="font-nunito font-bold text-[#1A4D2E]">+{a.pontos_dia} pts</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="font-inter text-gray-400 text-center py-4">Nenhuma atividade registrada ainda</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DashboardPais;
